/* ============================================================================
   OEI — UN MOTOR INOPERATIVO (One Engine Inoperative)

   QUÉ CAMBIA EN UN BIMOTOR
   ------------------------
   En un monomotor la pregunta es "¿puedo sostener el estacionario?". En un
   bimotor hay una segunda pregunta que decide la operación: "si se me para un
   motor AHORA, ¿el que queda alcanza?".

   Y no es la misma cuenta. Con los dos motores (AEO) el BK117 llega a 3350 kg;
   con uno solo, a nivel del mar y 15 °C, el peso máximo para sostener el
   estacionario fuera del efecto suelo baja a 2770 kg. Son 580 kg —siete
   personas— que separan "puedo despegar" de "puedo despegar y sobrevivir a una
   falla".

   Un operador que planifica solo con la carta AEO está planificando para el
   día en que todo funciona.

   LO QUE ESTE MÓDULO SÍ TIENE
   ---------------------------
   Las reglas del manual que están escritas como texto o tabla, no como curva:
   la reserva de régimen de ascenso, el crédito por viento y sus condiciones,
   los regímenes de potencia y la envolvente de azimut. Todo eso se transcribe
   sin pérdida.

   LO QUE NO TIENE
   ---------------
   Las curvas de peso máximo OEI por altitud y temperatura. Están publicadas
   como nomograma —Figura 5-2— y un nomograma escaneado no se digitaliza
   punto por punto con fiabilidad. Mientras no se carguen valores leídos del
   manual, la aplicación responde "sin carta OEI" en vez de un número.
   ========================================================================== */

/* ═══════════════════════════════════════════════════════════════════════════
   OEI ES UNA PREGUNTA DE BIMOTOR

   «Un motor inoperativo» significa que uno se paró y **el otro sigue**. La
   pregunta es si el que queda alcanza.

   En un monomotor esa pregunta NO EXISTE. Si se para el motor, se autorrota:
   no hay peso máximo que consultar ni reserva de ascenso que calcular.

   El panel se estaba dibujando con el AS350 cargado y mostraba «máximo 0 kg,
   excedido 2009,8 kg» — números del BK117 sobre una aeronave que no tiene
   segundo motor. Un cero ahí no dice «no aplica»: dice «no puedes despegar», y
   son cosas opuestas.
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * ¿Corresponde analizar OEI en esta aeronave?
 *
 * Sin el dato de motores devuelve `null`, no `false`: **no saber cuántos
 * motores tiene no es lo mismo que saber que tiene uno**. Con `null` el panel
 * se abstiene y lo dice, en vez de afirmar que no aplica sobre un bimotor cuyo
 * perfil quedó incompleto.
 */
export function correspondeOei(motores) {
  /* ═══ DOS FORMAS DEL MISMO DATO ═══

     El AS350 declara `motores: 1` y el BK117 `motores: { cantidad: 2, modelo:
     "..." }`. Son dos perfiles escritos en momentos distintos y las dos formas
     son razonables.

     La primera versión solo entendía el número, así que con el BK117 cargado
     el panel se habría apagado: **justo en el bimotor, que es la única
     aeronave donde el análisis hace falta**.

     Exigir una sola forma habría significado tocar el perfil del BK117 —que
     está verificado contra el RFM— para acomodar el código. Es al revés: el
     código se acomoda al dato. */
  const crudo = (motores && typeof motores === "object")
    ? motores.cantidad
    : motores;

  const n = Number(crudo);
  if (!Number.isFinite(n) || n <= 0) {
    return {
      corresponde: null,
      motivo: "El perfil de la aeronave no declara cuántos motores tiene. "
        + "Sin ese dato no se puede saber si el análisis OEI corresponde.",
    };
  }

  if (n === 1) {
    return {
      corresponde: false,
      motivo: "Es una aeronave monomotor. «Un motor inoperativo» supone que "
        + "queda otro funcionando: en un monomotor la falla se resuelve con "
        + "autorrotación, no con un peso máximo.",
    };
  }

  return { corresponde: true, motivo: null };
}

/**
 * Reserva de régimen de ascenso sobre el estacionario OEI.
 *
 * Transcrita del RFM BK117 B-2, sección 5.8A.3: para operaciones que exigen
 * régimen de ascenso además del estacionario, se RESTA peso al que da la
 * carta. Es una tabla de tres renglones, publicada como tabla.
 */
export const RESERVA_ASCENSO_OEI = [
  { regimenFtMin: 50, restaKg: 30 },
  { regimenFtMin: 100, restaKg: 60 },
  { regimenFtMin: 150, restaKg: 90 },
];

/** Regímenes de potencia del BK117 B-2, del encabezado de cada carta. */
export const REGIMENES_POTENCIA_BK117 = {
  despegue: { etiqueta: "Despegue", totC: 786, torquePct: 83, nota: "Sangrado de aire NO permitido" },
  oei30min: { etiqueta: "OEI 30 min", totC: 875, torquePct: 91.4 },
  oei25min: { etiqueta: "OEI 2,5 min", totC: 892, torquePct: 125 },
};

/**
 * Envolvente de viento para el estacionario fuera del efecto suelo.
 *
 * Del RFM 5.12 y Figura 5-16. La limitación de azimut solo está demostrada
 * hasta 3000 ft de altitud de densidad; por encima, las cartas suponen viento
 * nulo o de frente, así que un viento cruzado en altura queda FUERA de lo
 * demostrado y la app tiene que decirlo.
 */
export const ENVOLVENTE_VIENTO_BK117 = {
  omnidireccionalKt: 30,
  sectorProaKt: 60,
  sectorProaGrados: 15,
  altitudMaximaDensidadFt: 3000,
  vientoCruzadoMaxKt: 17,
};

/** Velocidad de mejor ascenso con un motor inoperativo. */
export const VY_OEI_KIAS = 65;

/**
 * Peso a restar por la reserva de ascenso pedida.
 *
 * Se interpola entre los renglones publicados. Por encima de 150 ft/min no se
 * extrapola: la tabla termina ahí y estirar la recta sería inventar una
 * capacidad que el fabricante no publicó.
 */
export function restaPorReservaAscenso(regimenFtMin) {
  if (!Number.isFinite(regimenFtMin) || regimenFtMin < 0) {
    return { restaKg: null, motivo: "Régimen de ascenso inválido." };
  }
  if (regimenFtMin === 0) return { restaKg: 0, motivo: null };

  const tabla = RESERVA_ASCENSO_OEI;
  const ultimo = tabla[tabla.length - 1];
  if (regimenFtMin > ultimo.regimenFtMin) {
    return {
      restaKg: null,
      motivo: `La tabla del manual llega a ${ultimo.regimenFtMin} ft/min. Para más régimen hay que ir al RFM.`,
    };
  }

  const exacto = tabla.find((t) => t.regimenFtMin === regimenFtMin);
  if (exacto) return { restaKg: exacto.restaKg, motivo: null };

  let previo = { regimenFtMin: 0, restaKg: 0 };
  for (const t of tabla) {
    if (regimenFtMin < t.regimenFtMin) {
      const f = (regimenFtMin - previo.regimenFtMin) / (t.regimenFtMin - previo.regimenFtMin);
      return { restaKg: Math.round(previo.restaKg + (t.restaKg - previo.restaKg) * f), motivo: null };
    }
    previo = t;
  }
  return { restaKg: null, motivo: "Fuera de la tabla publicada." };
}

/**
 * Crédito de peso por viento de frente.
 *
 * REGLA DEL MANUAL, sección 5.8A.3: salvo que la autoridad lo autorice, el
 * piloto NO puede acreditarse más del 50 % del aumento de performance que da
 * el viento de frente informado.
 *
 * El ejemplo del propio manual: a 15 °C y nivel del mar, con viento nulo el
 * peso máximo es 2770 kg y con 10 kt de frente sube a 2880 kg. Acreditando la
 * mitad: 2880 − ½(2880 − 2770) = 2825 kg.
 *
 * El viento de cola NO da crédito: el manual dice que debe evitarse.
 */
export function creditoPorViento({ pesoSinVientoKg, pesoConVientoKg, fraccionPermitida = 0.5 }) {
  if (!Number.isFinite(pesoSinVientoKg)) {
    return { pesoKg: null, motivo: "Falta el peso máximo con viento nulo." };
  }
  if (!Number.isFinite(pesoConVientoKg)) {
    return { pesoKg: pesoSinVientoKg, creditoKg: 0, motivo: "Sin dato de viento: se usa el peso con viento nulo." };
  }
  const bruto = pesoConVientoKg - pesoSinVientoKg;
  if (bruto <= 0) {
    // Con viento de cola o nulo el peso no sube. No se resta tampoco: la carta
    // de viento nulo ya es la condición conservadora.
    return {
      pesoKg: pesoSinVientoKg,
      creditoKg: 0,
      motivo: "El viento no aporta: se usa el peso con viento nulo. El manual pide evitar el viento de cola.",
    };
  }
  const acreditado = bruto * fraccionPermitida;
  return {
    pesoKg: Math.round((pesoSinVientoKg + acreditado) * 10) / 10,
    creditoKg: Math.round(acreditado * 10) / 10,
    creditoBrutoKg: Math.round(bruto * 10) / 10,
    motivo: `Se acredita el ${Math.round(fraccionPermitida * 100)} % de los ${Math.round(bruto)} kg que da el viento, según 5.8A.3.`,
  };
}

/**
 * ¿El viento está dentro de la envolvente demostrada de azimut?
 *
 * @param {number} azimutRelativoGrados 0 = de proa
 */
export function vientoDentroDeEnvolvente(azimutRelativoGrados, velocidadKt, altitudDensidadFt, env = ENVOLVENTE_VIENTO_BK117) {
  if (![azimutRelativoGrados, velocidadKt].every(Number.isFinite)) {
    return { dentro: null, motivo: "Faltan datos de viento." };
  }
  if (Number.isFinite(altitudDensidadFt) && altitudDensidadFt > env.altitudMaximaDensidadFt) {
    return {
      dentro: null,
      motivo: `La envolvente de azimut solo está demostrada hasta ${env.altitudMaximaDensidadFt} ft de altitud de densidad. ` +
        "Por encima, las cartas suponen viento nulo o de proa: un viento cruzado queda fuera de lo demostrado.",
    };
  }
  const az = Math.abs(((azimutRelativoGrados + 180) % 360) - 180);
  const limite = az <= env.sectorProaGrados ? env.sectorProaKt : env.omnidireccionalKt;
  return {
    dentro: velocidadKt <= limite,
    limiteKt: limite,
    motivo: velocidadKt <= limite
      ? null
      : `${velocidadKt} kt a ${Math.round(az)}° supera el límite demostrado de ${limite} kt. ` +
        "Fuera de la envolvente puede no haber margen de control suficiente.",
  };
}

/**
 * Evaluación OEI de un despegue.
 *
 * Devuelve el peso máximo admisible con un motor inoperativo y si el peso
 * bruto lo respeta. Sin carta OEI cargada NO se declara que cumple: se declara
 * que no se puede saber, que es distinto.
 */
export function evaluarOEI({
  pesoBrutoKg,
  pesoMaximoOeiSinVientoKg = null,
  pesoMaximoOeiConVientoKg = null,
  reservaAscensoFtMin = 0,
  fraccionCreditoViento = 0.5,
}) {
  const hallazgos = [];

  if (pesoMaximoOeiSinVientoKg === null) {
    return {
      valido: false,
      pesoMaximoKg: null,
      cumple: null,
      hallazgos: [{
        nivel: "precaucion",
        texto: "Sin carta OEI cargada para esta aeronave: no se puede afirmar que el despegue sea sostenible con un motor inoperativo.",
      }],
    };
  }

  const conViento = creditoPorViento({
    pesoSinVientoKg: pesoMaximoOeiSinVientoKg,
    pesoConVientoKg: pesoMaximoOeiConVientoKg,
    fraccionPermitida: fraccionCreditoViento,
  });
  if (conViento.motivo) hallazgos.push({ nivel: "info", texto: conViento.motivo });

  const reserva = restaPorReservaAscenso(reservaAscensoFtMin);
  if (reserva.restaKg === null) {
    hallazgos.push({ nivel: "precaucion", texto: reserva.motivo });
    return { valido: false, pesoMaximoKg: null, cumple: null, hallazgos };
  }
  if (reserva.restaKg > 0) {
    hallazgos.push({
      nivel: "info",
      texto: `Reserva de ${reservaAscensoFtMin} ft/min: se restan ${reserva.restaKg} kg al peso de la carta (RFM 5.8A.3).`,
    });
  }

  const pesoMaximoKg = Math.round((conViento.pesoKg - reserva.restaKg) * 10) / 10;
  const cumple = Number.isFinite(pesoBrutoKg) ? pesoBrutoKg <= pesoMaximoKg : null;

  if (cumple === false) {
    hallazgos.push({
      nivel: "alto",
      texto: `Con un motor inoperativo el máximo es ${pesoMaximoKg} kg y el peso bruto es ${Math.round(pesoBrutoKg)} kg: ` +
        `sobran ${Math.round(pesoBrutoKg - pesoMaximoKg)} kg. El despegue es posible con los dos motores, pero una falla ` +
        "en estacionario no sería sostenible.",
    });
  }
  if (cumple === null) {
    hallazgos.push({ nivel: "precaucion", texto: "Sin peso bruto no se puede comparar contra el máximo OEI." });
  }

  return {
    valido: true,
    pesoMaximoKg,
    pesoSinReservaKg: conViento.pesoKg,
    restaPorReservaKg: reserva.restaKg,
    creditoVientoKg: conViento.creditoKg ?? 0,
    cumple,
    margenKg: cumple === null ? null : Math.round((pesoMaximoKg - pesoBrutoKg) * 10) / 10,
    hallazgos,
  };
}
